// Roles page — master-detail layout with 20% sidebar and 80% detail panel.
// Route: /t/:slug/admin/roles

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { rolesApi } from '@/lib/api/usersRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { useToast } from '@/lib/hooks/use-toast'
import { RoleSidebar } from '@/components/features/roles/RoleSidebar'
import { RoleDetail } from '@/components/features/roles/RoleDetail'
import { CreateRoleDialog } from '@/components/features/roles/CreateRoleDialog'
import { EditRoleDialog } from '@/components/features/roles/EditRoleDialog'
import { DeleteRoleDialog } from '@/components/features/roles/DeleteRoleDialog'

export function RolesPage() {
  const { slug } = useTenant()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const rolesClient = rolesApi(api)

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: roles, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.roles.all(slug),
    queryFn: () => rolesClient.list(),
  })

  // Auto-select first role when data loads or selection becomes invalid
  useEffect(() => {
    if (!roles || roles.length === 0) {
      setSelectedRoleId(null)
      return
    }
    const stillExists = selectedRoleId && roles.some((r) => r.id === selectedRoleId)
    if (!stillExists && roles.length > 0) {
      setSelectedRoleId(roles[0]!.id)
    }
  }, [roles, selectedRoleId])

  const selectedRole = roles?.find((r) => r.id === selectedRoleId) ?? null

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => rolesClient.create(data),
    onSuccess: (newRole) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all(slug) })
      setSelectedRoleId(newRole.id)
      toast({ title: `Role "${newRole.name}" created` })
    },
    onError: (err) => {
      toast({
        title: 'Failed to create role',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string }) =>
      rolesClient.update(selectedRoleId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all(slug) })
      if (selectedRoleId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.roles.detail(slug, selectedRoleId) })
      }
      toast({ title: 'Role updated' })
    },
    onError: (err) => {
      toast({
        title: 'Failed to update role',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => rolesClient.delete(selectedRoleId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all(slug) })
      setSelectedRoleId(null) // will auto-select next role via effect
      toast({ title: 'Role deleted' })
    },
    // Error handling is done inside DeleteRoleDialog (409 blocker display)
  })

  // --- Error state ---

  if (error) {
    return (
      <ErrorState
        message="Failed to load roles"
        technicalDetail={error.message}
        onRetry={() => refetch()}
      />
    )
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] overflow-hidden rounded-md border">
      {/* Sidebar — 20% */}
      <div className="w-1/5 min-w-[200px] flex-shrink-0">
        <RoleSidebar
          roles={roles ?? []}
          selectedRoleId={selectedRoleId}
          onSelectRole={(role) => setSelectedRoleId(role.id)}
          onCreateClick={() => setCreateOpen(true)}
          isLoading={isLoading}
        />
      </div>

      {/* Detail panel — 80% */}
      <div className="flex-1 overflow-hidden">
        {selectedRole ? (
          <RoleDetail
            role={selectedRole}
            onEditClick={() => setEditOpen(true)}
            onDeleteClick={() => setDeleteOpen(true)}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            {isLoading ? 'Loading...' : 'No roles created yet. Click "New Role" to get started.'}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <CreateRoleDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={async (data) => {
          await createMutation.mutateAsync(data)
        }}
      />

      {selectedRole && (
        <>
          <EditRoleDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            currentName={selectedRole.name}
            currentDescription={selectedRole.description}
            onSubmit={async (data) => {
              await updateMutation.mutateAsync(data)
            }}
          />
          <DeleteRoleDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            roleName={selectedRole.name}
            onConfirm={async () => {
              await deleteMutation.mutateAsync()
            }}
          />
        </>
      )}
    </div>
  )
}
